"use client";

import { ListItem } from "@tiptap/extension-list";
import type { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey, Transaction } from "@tiptap/pm/state";

const TEXT_STYLE_MARK = "textStyle";

/** Get the first color from a node's content (from textStyle mark on inline nodes). */
function getFirstColorFromNode(node: Node): string | null {
    let color: string | null = null;
    node.descendants((n) => {
        if (color) return false;
        const mark = n.marks?.find((m) => m.type.name === TEXT_STYLE_MARK);
        if (mark?.attrs?.color) {
            color = mark.attrs.color as string;
            return false;
        }
        return true;
    });
    return color;
}

/** Parse fontSize to unitless number. "14px" → 14, "14" → 14. */
function parseFontSizeToUnitless(val: string | null): number | null {
    if (!val) return null;
    const num = parseInt(val, 10);
    return isNaN(num) ? null : num;
}

/** Get the first fontSize from a node's content (from textStyle mark on inline nodes). */
function getFirstFontSizeFromNode(node: Node): string | null {
    let fontSize: string | null = null;
    node.descendants((n) => {
        if (fontSize) return false;
        const mark = n.marks?.find((m) => m.type.name === TEXT_STYLE_MARK);
        if (mark?.attrs?.fontSize) {
            fontSize = mark.attrs.fontSize as string;
            return false;
        }
        return true;
    });
    return fontSize;
}

const syncMarkerColorPluginKey = new PluginKey("listItemMarkerColor");

/** ListItem extended with markerColor attribute – stored in the document so it survives ProseMirror updates. */
export const ListItemWithMarkerColor = ListItem.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            markerColor: {
                default: null,
                parseHTML: (el) =>
                    el.getAttribute("data-marker-color") ||
                    (el as HTMLElement).style.getPropertyValue("--marker-color") ||
                    null,
                renderHTML: (attrs) => {
                    const parts: string[] = [];
                    if (attrs.markerColor)
                        parts.push(`--marker-color: ${attrs.markerColor}`);
                    if (attrs.markerFontSize) {
                        const unitless = parseFontSizeToUnitless(
                            attrs.markerFontSize as string
                        );
                        parts.push(
                            unitless != null
                                ? `--marker-font-size: calc(${unitless} * var(--scene-font-scale, 1) * 1px)`
                                : `--marker-font-size: ${attrs.markerFontSize}`
                        );
                    }
                    if (parts.length === 0) return {};
                    const html: Record<string, string> = {
                        style: parts.join("; "),
                    };
                    if (attrs.markerColor)
                        html["data-marker-color"] = attrs.markerColor;
                    if (attrs.markerFontSize)
                        html["data-marker-font-size"] = attrs.markerFontSize;
                    return html;
                },
            },
            markerFontSize: {
                default: null,
                parseHTML: (el) =>
                    el.getAttribute("data-marker-font-size") ||
                    (el as HTMLElement).style.getPropertyValue(
                        "--marker-font-size"
                    ) ||
                    null,
                renderHTML: () => ({}), // style is rendered by markerColor
            },
        };
    },

    addProseMirrorPlugins() {
        const listItemType = this.type;
        return [
            new Plugin({
                key: syncMarkerColorPluginKey,
                appendTransaction(transactions, _oldState, newState) {
                    if (!transactions.some((tr) => tr.docChanged)) return null;
                    let tr: Transaction | null = null;
                    newState.doc.descendants((node, pos) => {
                        if (node.type !== listItemType) return;
                        const color = getFirstColorFromNode(node);
                        const fontSize = getFirstFontSizeFromNode(node);
                        const currentColor = node.attrs.markerColor;
                        const currentFontSize = node.attrs.markerFontSize;
                        const colorChanged = color !== currentColor;
                        const fontSizeChanged = fontSize !== currentFontSize;
                        if (colorChanged || fontSizeChanged) {
                            if (!tr) tr = newState.tr;
                            tr.setNodeMarkup(pos, undefined, {
                                ...node.attrs,
                                ...(colorChanged && { markerColor: color }),
                                ...(fontSizeChanged && { markerFontSize: fontSize }),
                            });
                        }
                    });
                    return tr;
                },
            }),
        ];
    },
});
